// src/components/books/modals/UserResponseTypeModal.jsx
import React, { useState } from 'react';
import { X, HelpCircle } from 'lucide-react';

export const UserResponseTypeModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  currentType = '', 
  currentVariable = '' 
}) => {
  const [responseType, setResponseType] = useState(currentType);
  const [variable, setVariable] = useState(currentVariable);
  const [config, setConfig] = useState({});
  const [showHelper, setShowHelper] = useState(false);

  const responseTypes = [
    {
      value: 'text',
      label: 'Text Input',
      description: 'Single line text input',
      config: {
        placeholder: 'Enter placeholder text...',
        maxLength: 100,
        required: true
      }
    },
    {
      value: 'textarea',
      label: 'Long Text',
      description: 'Multi-line text area',
      config: {
        placeholder: 'Enter placeholder text...',
        maxLength: 500,
        rows: 3,
        required: true
      }
    },
    {
      value: 'email',
      label: 'Email',
      description: 'Email address input with validation',
      config: {
        placeholder: 'Enter your email...',
        required: true
      }
    },
    {
      value: 'number',
      label: 'Number',
      description: 'Numeric input',
      config: {
        min: 0,
        max: 100,
        step: 1,
        required: true
      }
    },
    {
    value: 'math',
      label: 'Math Expression',
      description: 'Mathematical formula input with LaTeX support',
      config: {
        placeholder: 'Enter mathematical expression...',
        keyboards: ['geometric', 'algebra', 'trigonometry'],
        required: true
      }
    },
    {
      value: 'choice',
      label: 'Multiple Choice',
      description: 'Radio buttons or dropdown',
      config: {
        options: ['Option 1', 'Option 2', 'Option 3'],
        style: 'radio', // radio, dropdown
        required: true
      }
    },
    {
      value: 'rating',
      label: 'Rating',
      description: 'Star or numeric rating',
      config: {
        max: 5,
        style: 'stars', // stars, numbers
        required: true
      }
    },
    {
      value: 'date',
      label: 'Date',
      description: 'Date picker',
      config: {
        minDate: '',
        maxDate: '',
        required: true
      }
    },
    {
      value: 'file',
      label: 'File Upload',
      description: 'File upload input',
      config: {
        allowedTypes: ['image/*', '.pdf', '.doc'],
        maxSize: 5, // MB
        required: true
      }
    }
  ];

  const selectedType = responseTypes.find(type => type.value === responseType);

  const handleSave = () => {
    onSave({
      type: responseType,
      variable: variable,
      config: config
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">User Response Type</h2>
            <button
              onClick={() => setShowHelper(!showHelper)}
              className="p-1 text-gray-400 hover:text-blue-600"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Helper Guide */}
          {showHelper && (
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="font-medium text-green-900 mb-2">Response Types Guide</h3>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• Choose how users can respond to this message</li>
                <li>• Variable name stores the user's response</li>
                <li>• Use stored variables in later messages or conditions</li>
                <li>• Configure validation and appearance options</li>
              </ul>
            </div>
          )}

          {/* Variable Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Variable Name *
            </label>
            <input
              type="text"
              value={variable}
              onChange={(e) => setVariable(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="e.g., userName, email, rating"
            />
            <p className="text-xs text-gray-500 mt-1">
              Use this name to reference the response in later messages: {`{${variable}}`}
            </p>
          </div>

          {/* Response Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Response Type
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {responseTypes.map((type) => (
                <button
                  key={type.value}
                  onClick={() => {
                    setResponseType(type.value);
                    setConfig(type.config);
                  }}
                  className={`p-4 text-left border rounded-lg hover:bg-gray-50 ${
                    responseType === type.value 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-gray-200'
                  }`}
                >
                  <div className="font-medium">{type.label}</div>
                  <div className="text-sm text-gray-600 mt-1">{type.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Configuration Options */}
          {selectedType && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium mb-4">Configuration</h3>
              
              {/* Text/Email/Textarea configs */}
              {(['text', 'textarea', 'email'].includes(responseType)) && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Placeholder Text
                    </label>
                    <input
                      type="text"
                      value={config.placeholder || ''}
                      onChange={(e) => setConfig({...config, placeholder: e.target.value})}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Length
                    </label>
                    <input
                      type="number"
                      value={config.maxLength || ''}
                      onChange={(e) => setConfig({...config, maxLength: parseInt(e.target.value)})}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                </div>
              )}

              {/* Number configs */}
              {responseType === 'number' && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Min</label>
                    <input
                      type="number"
                      value={config.min || ''}
                      onChange={(e) => setConfig({...config, min: parseInt(e.target.value)})}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max</label>
                    <input
                      type="number"
                      value={config.max || ''}
                      onChange={(e) => setConfig({...config, max: parseInt(e.target.value)})}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Step</label>
                    <input
                      type="number"
                      value={config.step || ''}
                      onChange={(e) => setConfig({...config, step: parseInt(e.target.value)})}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                </div>
              )}

              {/* Choice configs */}
              {responseType === 'choice' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Style
                    </label>
                    <select
                      value={config.style || 'radio'}
                      onChange={(e) => setConfig({...config, style: e.target.value})}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="radio">Radio Buttons</option>
                      <option value="select">Dropdown</option>
                      <option value="buttons">Styled Buttons</option>
                      <option value="multiselect">Multiselect</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Options (one per line)
                    </label>
                    <textarea
                      value={(config.options || []).join('\n')}
                      onChange={(e) => setConfig({
                        ...config, 
                        options: e.target.value.split('\n').filter(Boolean)
                      })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      rows="4"
                      placeholder="Option 1&#10;Option 2&#10;Option 3"
                    />
                  </div>
                </div>
              )}

              {responseType === 'math' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Placeholder Text
                  </label>
                  <input
                    type="text"
                    value={config.placeholder || 'Enter mathematical expression...'}
                    onChange={(e) => setConfig({...config, placeholder: e.target.value})}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    placeholder="Enter mathematical expression..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Virtual Keyboards
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'geometric', label: 'Geometry' },
                      { value: 'algebra', label: 'Algebra' },
                      { value: 'trigonometry', label: 'Trigonometry' },
                      { value: 'calculus', label: 'Calculus' },
                      { value: 'statistics', label: 'Statistics' }
                    ].map((keyboard) => (
                      <label key={keyboard.value} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={(config.keyboards || []).includes(keyboard.value)}
                          onChange={(e) => {
                            const keyboards = config.keyboards || [];
                            if (e.target.checked) {
                              setConfig({...config, keyboards: [...keyboards, keyboard.value]});
                            } else {
                              setConfig({...config, keyboards: keyboards.filter(k => k !== keyboard.value)});
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                        <span className="text-sm text-gray-700">{keyboard.label}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Select which mathematical keyboards to show
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Validation Options
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={config.validateLatex || false}
                        onChange={(e) => setConfig({...config, validateLatex: e.target.checked})}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Validate LaTeX syntax</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={config.allowEmpty || false}
                        onChange={(e) => setConfig({...config, allowEmpty: e.target.checked})}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">Allow empty responses</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

              {/* Rating configs */}
              {responseType === 'rating' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Rating
                    </label>
                    <input
                      type="number"
                      value={config.max || 5}
                      onChange={(e) => setConfig({...config, max: parseInt(e.target.value)})}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                      min="1"
                      max="10"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Style
                    </label>
                    <select
                      value={config.style || 'stars'}
                      onChange={(e) => setConfig({...config, style: e.target.value})}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    >
                      <option value="stars">Stars</option>
                      <option value="numbers">Numbers</option>
                      <option value="emoji">Emoji</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Required checkbox */}
              <div className="flex items-center gap-2 mt-4">
                <input
                  type="checkbox"
                  id="required"
                  checked={config.required || false}
                  onChange={(e) => setConfig({...config, required: e.target.checked})}
                  className="rounded border-gray-300"
                />
                <label htmlFor="required" className="text-sm text-gray-700">
                  Required field
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!variable || !responseType}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Save Response Type
          </button>
        </div>
      </div>
    </div>
  );
};